import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { Box, MenuItem, Select, Skeleton, TextField, Typography } from "@mui/material";
import { useFormik } from "formik";
import * as yup from "yup";
import * as Sentry from "@sentry/react";
import {
  AccordionCard,
  CountrySelect,
  FieldWrapper,
  PageHeader,
  SegmentedFilter,
  StepActions,
  TabBofuV2,
} from "@components/ui_v2";
import { RootState, useAppDispatch } from "@store/store";
import { setModalErrorVisible } from "@store/slices/appSlice";
import { useLazyGetReferenceTypesQuery } from "@api/catalogs";
import { useLazyGetClientsSuppliersByRfcQuery } from "@api/proveedorClienteApi";
import { useLazyGetReferencesQuery, usePatchReferencesMutation, usePostReferencesMutation } from "@api/referenciasApi";
import { usePutApplicationCompletedMutation } from "@api/applications";
import { ApiError, ClientSupplier, ReferenceType } from "@interfaces/response";
import { ReferenceStructure } from "@interfaces/request";
import { CLIENT_RFC_NOT_VALID, PATHS_EXPEDIENTE_V2, REFERENCES_CODES } from "@helpers/constantes";
import { getFormikArrayError } from "@helpers/funciones";
import { EMAIL, PHONE_NUMBER } from "@utils/expreciones";
import { UUID } from "crypto";

/** Referencias que pide el expediente por cada tipo. */
const REFERENCIAS_POR_TIPO = 3;

/** Alto del skeleton mientras carga; aproxima las tres tarjetas cerradas. */
const ALTO_SKELETON = 260;

type TabId = "clientes" | "proveedores";

/**
 * Lo que cambia entre las dos sub-secciones. El expediente legacy las resuelve en dos pantallas
 * (`ReferenciasClientes` y `ReferenciasProveedores`) que solo difieren en esto; el diseño las
 * fusiona en una con sub-tabs.
 */
const TABS = {
  clientes: {
    label: "Clientes",
    singular: "Cliente",
    referenceCode: REFERENCES_CODES.cliente,
    /** El backend guarda el nombre y el RFC en campos distintos según el tipo. */
    campoNombre: "company_name",
    campoRfc: "company_rfc",
    idExt: "3333",
    ayuda: "Agrega 3 clientes de la empresa que podamos contactar como referencia comercial.",
    placeholder: "Nombre o razón social del cliente",
  },
  proveedores: {
    label: "Proveedores",
    singular: "Proveedor",
    referenceCode: REFERENCES_CODES.proveedor,
    campoNombre: "supplier_name",
    campoRfc: "supplier_rfc",
    idExt: "4444",
    ayuda: "Agrega 3 proveedores de la empresa que podamos contactar como referencia comercial.",
    placeholder: "Nombre o razón social del proveedor",
  },
} as const;

interface Referencia {
  id: string;
  /** RFC del cliente o proveedor elegido del catálogo del SAT. */
  rfc: string;
  contact_name: string;
  email: string;
  country_code: string;
  phone: string;
  phone_extension: string;
}

interface FormValues {
  clientes: Referencia[];
  proveedores: Referencia[];
}

const referenciaVacia = (): Referencia => ({
  id: "",
  rfc: "",
  contact_name: "",
  email: "",
  country_code: "+52",
  phone: "",
  phone_extension: "",
});

const listaVacia = () => Array.from({ length: REFERENCIAS_POR_TIPO }, referenciaVacia);

/** Una referencia está completa cuando tiene sus cinco datos obligatorios. */
const estaCompleta = (item: Referencia) =>
  Boolean(item.rfc && item.contact_name && item.email && item.country_code && item.phone);

/** Firma del teléfono, para detectar repetidos: el legacy compara lada + número + extensión. */
const firmaTelefono = (item: Referencia) =>
  (item.country_code || "") + (item.phone || "") + (item.phone_extension || "");

/**
 * Reglas de una referencia, copiadas del expediente legacy. La unicidad mira las otras referencias
 * de la **misma** sub-sección: formik entrega los `values` como contexto de yup, así que se leen de
 * ahí en vez de del propio `formik` —que volvería circular el tipo—.
 */
const referenciasSchema = (tab: TabId) =>
  yup.array().of(
    yup.object().shape({
      rfc: yup.string().required("Campo requerido"),
      country_code: yup.string().required("Campo requerido"),
      phone: yup
        .string()
        .required("Campo requerido")
        .matches(PHONE_NUMBER, "Número de teléfono inválido")
        .test("unique", "El teléfono ya se encuentra registrado.", function () {
          const lista = (this.options.context as FormValues | undefined)?.[tab] ?? [];
          const propia = firmaTelefono(this.parent as Referencia);
          return lista.filter((item) => firmaTelefono(item) === propia).length <= 1;
        }),
      contact_name: yup
        .string()
        .required("Campo requerido")
        .test("unique", "El nombre de contacto ya se encuentra registrado.", function (value) {
          const lista = (this.options.context as FormValues | undefined)?.[tab] ?? [];
          const normalizar = (nombre: string) => (nombre || "").replaceAll(" ", "").toLowerCase();
          return lista.filter((item) => normalizar(item.contact_name) === normalizar(value)).length <= 1;
        }),
      email: yup
        .string()
        .required("Campo requerido")
        .matches(EMAIL, "Asegúrate de incluir el @ y los puntos necesarios (.com .mx)")
        .test("unique", "El email ya se encuentra registrado.", function (value) {
          const lista = (this.options.context as FormValues | undefined)?.[tab] ?? [];
          return lista.filter((item) => item.email === value).length <= 1;
        }),
    }),
  );

const validationSchema = yup.object().shape({
  clientes: referenciasSchema("clientes"),
  proveedores: referenciasSchema("proveedores"),
});

/** Completa la lista del backend hasta las 3 tarjetas que pide el expediente. */
const aReferencias = (registros: ReferenceStructure[], campoRfc: "company_rfc" | "supplier_rfc"): Referencia[] => {
  const guardadas = registros.slice(0, REFERENCIAS_POR_TIPO).map((item) => ({
    id: item.id as string,
    rfc: item[campoRfc] || "",
    contact_name: item.contact_name,
    email: item.email,
    country_code: item.code_phone_number,
    phone: item.phone_number,
    phone_extension: item.phone_extension,
  }));

  return [...guardadas, ...listaVacia().slice(guardadas.length)];
};

export const ExpReferencias = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const client = useSelector((state: RootState) => state.register.personal_data);
  const company = useSelector((state: RootState) => state.register.company);
  const application = useSelector((state: RootState) => state.register.application);

  const [getReferenceTypes] = useLazyGetReferenceTypesQuery();
  const [getClientsSuppliersByRFC] = useLazyGetClientsSuppliersByRfcQuery();
  const [getReferences] = useLazyGetReferencesQuery();
  const [triggerPostReferences] = usePostReferencesMutation();
  const [triggerPatchReferences] = usePatchReferencesMutation();
  const [putApplicationCompleted] = usePutApplicationCompletedMutation();

  const [isLoading, setIsLoading] = useState(true);
  const [tab, setTab] = useState<TabId>("clientes");
  const [openIdx, setOpenIdx] = useState<number | null>(0);
  /** Tipo de referencia y catálogo del SAT de cada sub-sección. */
  const [tipos, setTipos] = useState<Record<TabId, ReferenceType | null>>({ clientes: null, proveedores: null });
  const [catalogos, setCatalogos] = useState<Record<TabId, ClientSupplier[]>>({ clientes: [], proveedores: [] });

  const volverAlExpediente = () => navigate(PATHS_EXPEDIENTE_V2.empresa.path);

  const formik = useFormik<FormValues>({
    initialValues: { clientes: listaVacia(), proveedores: listaVacia() },
    validationSchema,
    onSubmit: async (_values, { setSubmitting }) => {
      try {
        setSubmitting(true);

        await putApplicationCompleted({
          id_sol: application.id,
          body: { references_status: "COMPLETO" },
        }).unwrap();

        volverAlExpediente();
      } catch (error) {
        Sentry.captureException(error, { extra: { component: "ExpReferencias-submit" } });
        dispatch(setModalErrorVisible({ open: true, error: error as ApiError }));
      } finally {
        setSubmitting(false);
      }
    },
  });

  const { setFieldValue, validateForm } = formik;

  /**
   * Guardado parcial, como el legacy: al salir de un campo se persiste la referencia completa —
   * `POST` la primera vez y `PATCH` después—. Una referencia incompleta o con errores no se manda.
   */
  const guardarReferencia = async (tabId: TabId, index: number) => {
    const referencia = formik.values[tabId][index];
    const tipo = tipos[tabId];
    const config = TABS[tabId];

    if (!tipo || !estaCompleta(referencia)) return;

    const errores = await validateForm();
    if (errores[tabId]?.[index]) return;

    try {
      const elegido = catalogos[tabId].find((item) => item.rfc === referencia.rfc);

      const body = {
        client_details_id: client.id,
        id_ext: config.idExt,
        reference_type_id: tipo.id,
        [config.campoNombre]: elegido?.name,
        [config.campoRfc]: elegido?.rfc,
        contact_name: referencia.contact_name,
        email: referencia.email,
        code_phone_number: referencia.country_code,
        phone_number: referencia.phone,
        phone_extension: referencia.phone_extension,
        percentage_share: Number(elegido?.share),
      };

      if (referencia.id) {
        await triggerPatchReferences({ referenceId: referencia.id as UUID, body }).unwrap();
      } else {
        const creada = await triggerPostReferences(body).unwrap();
        setFieldValue(`${tabId}[${index}].id`, creada.data.id);
      }
    } catch (error) {
      /*
       * TODO: paridad con el legacy — el guardado por campo falla en silencio. El usuario cree que
       * su referencia quedó registrada y solo se entera al intentar continuar.
       */
      Sentry.captureException(error, { extra: { component: "ExpReferencias-guardarReferencia" } });
    }
  };

  /** Carga los dos tipos de referencia, el catálogo del SAT y lo ya capturado de cada sub-sección. */
  const getInitialData = useCallback(async () => {
    try {
      setIsLoading(true);
      if (!client.id) return;

      const [tiposReferencia, catalogoSat] = await Promise.all([
        getReferenceTypes().unwrap(),
        getClientsSuppliersByRFC(String(company?.rfc)).unwrap(),
      ]);

      const tipoClientes = tiposReferencia.find((item) => item.code === TABS.clientes.referenceCode) ?? null;
      const tipoProveedores = tiposReferencia.find((item) => item.code === TABS.proveedores.referenceCode) ?? null;

      setTipos({ clientes: tipoClientes, proveedores: tipoProveedores });
      setCatalogos({ clientes: catalogoSat.clients, proveedores: catalogoSat.suppliers });

      const [guardadasClientes, guardadasProveedores] = await Promise.all([
        tipoClientes
          ? getReferences({ clientDetailsId: client.id, referenceTypeId: tipoClientes.id }).unwrap()
          : Promise.resolve([] as ReferenceStructure[]),
        tipoProveedores
          ? getReferences({ clientDetailsId: client.id, referenceTypeId: tipoProveedores.id }).unwrap()
          : Promise.resolve([] as ReferenceStructure[]),
      ]);

      setFieldValue("clientes", aReferencias(guardadasClientes, TABS.clientes.campoRfc));
      setFieldValue("proveedores", aReferencias(guardadasProveedores, TABS.proveedores.campoRfc));

      // Tras varios `setFieldValue` seguidos el esquema no revalida solo.
      setTimeout(() => validateForm(), 200);
    } catch (error) {
      Sentry.captureException(error, { extra: { component: "ExpReferencias-initial_fetch" } });
      dispatch(setModalErrorVisible({ open: true, error: error as ApiError }));
    } finally {
      setIsLoading(false);
    }
  }, [
    client.id,
    company?.rfc,
    getReferenceTypes,
    getClientsSuppliersByRFC,
    getReferences,
    setFieldValue,
    validateForm,
    dispatch,
  ]);

  useEffect(() => {
    getInitialData();
  }, [getInitialData]);

  const config = TABS[tab];
  const lista = formik.values[tab];
  const completas = (tabId: TabId) => formik.values[tabId].filter(estaCompleta).length;
  const todasCompletas =
    completas("clientes") === REFERENCIAS_POR_TIPO && completas("proveedores") === REFERENCIAS_POR_TIPO;
  const bloqueado = isLoading || formik.isSubmitting;

  /** Opciones del catálogo, sin las ya elegidas en otra tarjeta ni los RFC genéricos. */
  const opcionesDisponibles = (index: number) =>
    catalogos[tab]
      .filter((item) => !lista.some((ref, refIndex) => refIndex !== index && ref.rfc === item.rfc))
      .filter((item) => !CLIENT_RFC_NOT_VALID.includes(item.rfc))
      .sort((a, b) => a.name.localeCompare(b.name));

  const cambiarTab = (nuevo: TabId) => {
    setTab(nuevo);
    const primeraIncompleta = formik.values[nuevo].findIndex((item) => !estaCompleta(item));
    setOpenIdx(primeraIncompleta === -1 ? null : primeraIncompleta);
  };

  /** Al salir de un campo: marca el `touched` de formik y persiste si la referencia ya está completa. */
  const onBlurCampo = (event: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>, index: number) => {
    formik.handleBlur(event);
    guardarReferencia(tab, index);
  };

  return (
    <Box>
      <PageHeader
        eyebrow="Expediente · Empresa · Referencias comerciales"
        title="Referencias comerciales"
        subtitle="Necesitamos 3 referencias de clientes y 3 referencias de proveedores de la empresa solicitante para validar su operación comercial."
      />

      <TabBofuV2 active="empresa" />

      <Box sx={{ mb: "14px" }}>
        <SegmentedFilter
          variant="subtabs"
          value={tab}
          onChange={(valor) => cambiarTab(valor as TabId)}
          testIdPrefix="expediente-referencias-tab"
          options={[
            {
              value: "clientes",
              label: TABS.clientes.label,
              count: `${completas("clientes")}/${REFERENCIAS_POR_TIPO}`,
            },
            {
              value: "proveedores",
              label: TABS.proveedores.label,
              count: `${completas("proveedores")}/${REFERENCIAS_POR_TIPO}`,
            },
          ]}
        />
      </Box>

      <Typography variant="body2" sx={{ color: "text.secondary", mb: "18px" }}>
        {config.ayuda}
      </Typography>

      {isLoading ? (
        <Skeleton variant="rectangular" animation="wave" sx={{ height: ALTO_SKELETON, borderRadius: "12px" }} />
      ) : (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
          {lista.map((referencia, index) => {
            const completa = estaCompleta(referencia);
            const capturada = Boolean(referencia.rfc || referencia.contact_name || referencia.phone);
            const nombreElegido = catalogos[tab].find((item) => item.rfc === referencia.rfc)?.name;

            return (
              <AccordionCard
                key={`${tab}_${index}`}
                index={index + 1}
                title={`${config.singular} ${index + 1}`}
                summary={
                  completa
                    ? `${nombreElegido ?? referencia.rfc} · ${referencia.contact_name} · ${referencia.phone}`
                    : capturada
                      ? "Datos parciales · faltan campos"
                      : "Sin información"
                }
                status={completa ? "COMPLETO" : "PENDIENTE"}
                open={openIdx === index}
                onToggle={() => setOpenIdx((actual) => (actual === index ? null : index))}
              >
                <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 2 }}>
                  <FieldWrapper label={config.singular} full error={getFormikArrayError(formik, tab, index, "rfc")}>
                    <Select
                      size="lendia_input"
                      name={`${tab}[${index}].rfc`}
                      fullWidth
                      displayEmpty
                      value={referencia.rfc}
                      onChange={formik.handleChange}
                      onBlur={() => guardarReferencia(tab, index)}
                      disabled={bloqueado}
                      inputProps={{ "data-testid": `expediente-referencias-${tab}-${index}-select` }}
                    >
                      <MenuItem value="">{config.placeholder}</MenuItem>
                      {opcionesDisponibles(index).map((item) => (
                        <MenuItem key={item.rfc} value={item.rfc}>
                          {item.name.toUpperCase()}
                        </MenuItem>
                      ))}
                    </Select>
                  </FieldWrapper>

                  <FieldWrapper
                    label="Nombre completo del contacto"
                    full
                    error={getFormikArrayError(formik, tab, index, "contact_name")}
                  >
                    <TextField
                      size="lendia_input"
                      name={`${tab}[${index}].contact_name`}
                      fullWidth
                      autoComplete="off"
                      placeholder="Nombre del contacto"
                      value={referencia.contact_name}
                      onChange={formik.handleChange}
                      onBlur={(event) => onBlurCampo(event, index)}
                      disabled={bloqueado}
                      inputProps={{ "data-testid": `expediente-referencias-${tab}-${index}-contacto-input` }}
                    />
                  </FieldWrapper>

                  <FieldWrapper label="Correo electrónico" error={getFormikArrayError(formik, tab, index, "email")}>
                    <TextField
                      size="lendia_input"
                      name={`${tab}[${index}].email`}
                      fullWidth
                      autoComplete="off"
                      placeholder="contacto@empresa.com"
                      value={referencia.email}
                      onChange={(event) =>
                        setFieldValue(`${tab}[${index}].email`, (event.target.value || "").toLowerCase())
                      }
                      onBlur={(event) => onBlurCampo(event, index)}
                      disabled={bloqueado}
                      inputProps={{
                        maxLength: 100,
                        "data-testid": `expediente-referencias-${tab}-${index}-email-input`,
                      }}
                    />
                  </FieldWrapper>

                  <FieldWrapper
                    label="Teléfono de contacto"
                    error={
                      getFormikArrayError(formik, tab, index, "country_code") ||
                      getFormikArrayError(formik, tab, index, "phone")
                    }
                  >
                    <Box sx={{ display: "flex", gap: 1 }}>
                      <CountrySelect
                        value={referencia.country_code}
                        onChange={(valor) => setFieldValue(`${tab}[${index}].country_code`, valor)}
                        disabled={bloqueado}
                      />
                      <TextField
                        autoComplete="off"
                        size="lendia_input"
                        name={`${tab}[${index}].phone`}
                        fullWidth
                        placeholder="55 1234 5678"
                        value={referencia.phone}
                        onChange={(event) =>
                          setFieldValue(`${tab}[${index}].phone`, event.target.value.replace(/[^0-9]/g, ""))
                        }
                        onBlur={(event) => onBlurCampo(event, index)}
                        disabled={bloqueado}
                        inputProps={{
                          maxLength: 10,
                          inputMode: "numeric",
                          "data-testid": `expediente-referencias-${tab}-${index}-telefono-input`,
                        }}
                      />
                      <TextField
                        autoComplete="off"
                        size="lendia_input"
                        name={`${tab}[${index}].phone_extension`}
                        placeholder="Ext."
                        value={referencia.phone_extension}
                        onChange={(event) =>
                          setFieldValue(`${tab}[${index}].phone_extension`, event.target.value.replace(/[^0-9]/g, ""))
                        }
                        onBlur={(event) => onBlurCampo(event, index)}
                        disabled={bloqueado}
                        sx={{ maxWidth: 96 }}
                        inputProps={{
                          maxLength: 5,
                          inputMode: "numeric",
                          "data-testid": `expediente-referencias-${tab}-${index}-extension-input`,
                        }}
                      />
                    </Box>
                  </FieldWrapper>
                </Box>
              </AccordionCard>
            );
          })}
        </Box>
      )}

      <StepActions
        onBack={volverAlExpediente}
        onNext={tab === "clientes" ? () => cambiarTab("proveedores") : formik.submitForm}
        backLabel="Regresar al Expediente"
        nextLabel={tab === "clientes" ? "Continuar a proveedores" : "Marcar como completo"}
        nextDisabled={bloqueado || (tab === "proveedores" && !todasCompletas)}
        backTestId="expediente-referencias-back-button"
        nextTestId="expediente-referencias-continue-button"
      />
    </Box>
  );
};
